import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus,
  UserX,
  Ban,
  Shield,
  ShieldCheck,
  X,
  Loader2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { notifyRolesChanged } from "@/lib/liveRoles";

/**
 * LiveHostControlMenu — opens when the host taps a viewer's avatar inside
 * the Live room. Six actions map to `/api/live/{sid}/…` moderation
 * endpoints. Every action is idempotent + best-effort; toast reports the
 * result and closes the sheet.
 *
 * Session-scoped: mod/admin roles + bans wipe automatically when the
 * host ends the stream (see backend `cleanup_stream_moderation`).
 */
export default function LiveHostControlMenu({
  streamId,
  target,           // { user_id, handle, display_name, avatar_url, role }
  isHost,
  actorRole,        // Pair 3: "host" | "admin" — the stream Admin gets every moderation action except the Admin seat
  onClose,
  onKicked,         // parent callback so the viewers drawer can refresh
}) {
  const [busy, setBusy] = useState(null);

  if (!target) return null;
  const currentRole = target.role || "viewer";
  const isHostTarget = currentRole === "host";
  const actor = actorRole || (isHost ? "host" : "viewer");
  const canModerate = actor === "host" || actor === "admin";
  const adminOnAdmin = actor === "admin" && currentRole === "admin";

  const call = async (label, fn) => {
    if (busy) return;
    setBusy(label);
    try {
      await fn();
      toast.success(`${label} ✓`);
      notifyRolesChanged();
      onKicked?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed.`);
    } finally {
      setBusy(null);
    }
  };

  const invite = () =>
    call("Invite sent", () =>
      api.post(`/live/${streamId}/invite-to-join`, { user_id: target.user_id }),
    );
  const kick = () =>
    call("Kicked", () =>
      api.post(`/live/${streamId}/kick`, { user_id: target.user_id }),
    );
  const ban = () =>
    call("Banned", () =>
      api.post(`/live/${streamId}/ban`, { user_id: target.user_id }),
    );
  const makeMod = () =>
    call("Made moderator", () =>
      api.post(`/live/${streamId}/roles/grant`, {
        user_id: target.user_id,
        role: "moderator",
      }),
    );
  const makeAdmin = () =>
    call("Made stream admin", () =>
      api.post(`/live/${streamId}/roles/grant`, {
        user_id: target.user_id,
        role: "admin",
      }),
    );
  const revokeRole = () =>
    call("Role revoked", () =>
      api.post(`/live/${streamId}/roles/revoke`, { user_id: target.user_id }),
    );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70"
      onClick={onClose}
      data-testid="live-host-control-menu"
    >
      <div
        className="w-full sm:max-w-sm bg-zinc-950 border-t sm:border sm:rounded-xl border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <Avatar className="w-11 h-11 border border-white/10">
            <AvatarImage src={target.avatar_url} />
            <AvatarFallback className="bg-rose-600 text-white text-sm">
              {target.display_name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-semibold truncate">
              {target.display_name || target.handle}
            </div>
            <div className="text-[11px] text-zinc-500 truncate">
              {target.display_name}
              {currentRole !== "viewer" && (
                <span className="ml-2 text-amber-300 uppercase tracking-widest text-[9px]">
                  {currentRole}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="text-white/60 hover:text-white p-1"
            onClick={onClose}
            aria-label="Close"
            data-testid="live-host-control-close"
          >
            <X size={18} />
          </button>
        </div>

        {isHostTarget ? (
          <div className="p-6 text-center text-sm text-zinc-400">
            The host cannot be moderated from this menu.
          </div>
        ) : !canModerate ? (
          <div className="p-6 text-center text-sm text-zinc-400">
            Only the host or the stream Admin can moderate this live.
          </div>
        ) : adminOnAdmin ? (
          <div className="p-6 text-center text-sm text-zinc-400" data-testid="host-menu-admin-seat-locked">
            Only the host can change the stream Admin.
          </div>

        ) : (
          <div className="divide-y divide-white/5" role="menu">
            <MenuRow
              icon={UserPlus}
              label="Invite to Join Live"
              hint="Send a co-host invite."
              disabled={busy !== null}
              busy={busy === "Invite sent"}
              onClick={invite}
              testId="host-menu-invite"
            />
            <MenuRow
              icon={UserX}
              label="Kick from Live"
              hint="They can return unless banned."
              tone="warn"
              disabled={busy !== null}
              busy={busy === "Kicked"}
              onClick={kick}
              testId="host-menu-kick"
            />
            <MenuRow
              icon={Ban}
              label="Ban from Live"
              hint="Blocks re-entry for this stream."
              tone="danger"
              disabled={busy !== null}
              busy={busy === "Banned"}
              onClick={ban}
              testId="host-menu-ban"
            />
            {currentRole === "moderator" || currentRole === "admin" ? (
              <MenuRow
                icon={Shield}
                label={`Remove ${currentRole === "admin" ? "Stream Admin" : "Moderator"}`}
                hint="Revoke elevated permissions immediately."
                disabled={busy !== null}
                busy={busy === "Role revoked"}
                onClick={revokeRole}
                testId="host-menu-role-revoke"
              />
            ) : (
              <>
                <MenuRow
                  icon={Shield}
                  label="Make Moderator"
                  hint="Can only view and deny waiting guests. Expires when live ends."
                  disabled={busy !== null}
                  busy={busy === "Made moderator"}
                  onClick={makeMod}
                  testId="host-menu-make-moderator"
                />
                {actor === "host" && (
                  <MenuRow
                    icon={ShieldCheck}
                    label="Make Stream Admin"
                    hint="One per Live — can do everything you can, except end or change the Live."
                    disabled={busy !== null}
                    busy={busy === "Made stream admin"}
                    onClick={makeAdmin}
                    testId="host-menu-make-admin"
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MenuRow({ icon: Icon, label, hint, tone, busy, disabled, onClick, testId }) {
  const toneClasses =
    tone === "danger"
      ? "text-rose-400 hover:bg-rose-500/10"
      : tone === "warn"
        ? "text-amber-300 hover:bg-amber-500/10"
        : "text-white hover:bg-white/5";
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left flex items-center gap-3 px-4 py-3 transition ${toneClasses} disabled:opacity-40`}
      data-testid={testId}
    >
      <span className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-[11px] text-zinc-500 mt-0.5">{hint}</span>
      </span>
    </button>
  );
}
